import React, { useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import axios from "axios";
import { RotateCcw } from "lucide-react";
import { toast } from "@jammable/ui-core";
import { Spinner } from "@jammable/ui-components/Loader";
import { useAuth } from "../../context/AuthProvider";
import { requireIdToken } from "../../utils/firebaseAuth";
import { getSubscriptionStatus } from "../../utils/tools";
import { track } from "../../utils/analytics";
import { useLocale, useT } from "@jammable/ui-core/src/i18n";

// Opens only when someone out of passes asks to try again.
const CreatorUpsellSheet = dynamic(
  () => import("../upsell/CreatorUpsellSheet"),
  {
    ssr: false,
  },
);

const trainAgainLink =
  "tw-flex tw-h-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-[15px] tw-font-extrabold tw-text-jam-ink tw-no-underline tw-transition-colors hover:tw-bg-jam-line";

// The API's refusal messages (lib/train/retry.ts) are English. On /es and /pt
// the same lines are shown from messages/train.ts, by failure name; anything
// not listed here keeps the API's wording.
const REFUSAL_BY_FAILURE = {
  "f0-empty": "train.retry.refuse.cleanVocals",
  "dataset-empty": "train.retry.refuse.formats",
  "input-empty": "train.retry.refuse.newSamples",
  "input-url-invalid": "train.retry.refuse.newSamples",
  "input-undecodable": "train.retry.refuse.saveAs",
  ffmpeg: "train.retry.refuse.saveAs",
};
function refusalText(t, locale, refusal, failureCode) {
  const message = refusal?.message;
  if (!message || locale === "en") return message;
  if (refusal.code === "repeats" && REFUSAL_BY_FAILURE[failureCode]) {
    return t(REFUSAL_BY_FAILURE[failureCode]);
  }
  if (refusal.code === "files-missing") {
    return t("train.retry.refuse.filesMissing");
  }
  const tries = /tried (\d+) times/.exec(message)?.[1];
  if (refusal.code === "retry-cap" && tries) {
    return t("train.retry.refuse.retryCap", { n: tries });
  }
  return message;
}

/**
 * The failed voice card's actions: "Try again" on the same samples
 * (api/model/retryTraining), or why not and what to do instead. `model.retry`
 * comes from getMyVoices (lib/train/retry).
 */
export default function RetryTraining({ model, onRetried }) {
  const { user, updateUser } = useAuth();
  const t = useT();
  const { locale } = useLocale();
  const [step, setStep] = useState("idle"); // idle | confirm | busy
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const retry = model?.retry;

  const plan = user?.subscriptions
    ? getSubscriptionStatus(user.subscriptions)
    : "free";
  const unlimited = plan === "creator" || plan === "power";
  const passes = user?.customModels ?? 0;
  const outOfPasses = !unlimited && passes < 1;
  // Same split as /train (TrainInfoStage): Basic and early adopters are
  // offered Creator; everyone else buys a pass in the shop.
  const offerCreator =
    outOfPasses && (plan === "basic" || plan === "early adopter");

  const shown = refusal ?? retry?.refusal;
  const shownText = refusalText(t, locale, shown, retry?.failureCode);
  if (!retry || !retry.canRetry || refusal) {
    return (
      <div className="tw-mt-auto tw-flex tw-flex-col tw-gap-2">
        {shownText && (
          <p className="tw-m-0 tw-text-[13px] tw-font-semibold tw-leading-snug tw-text-jam-muted">
            {shownText}
          </p>
        )}
        <Link href="/train" className={trainAgainLink}>
          {t("train.retry.trainAgain")}
        </Link>
      </div>
    );
  }

  const start = async () => {
    setStep("busy");
    try {
      const token = await requireIdToken();
      const { data } = await axios.post(
        "/api/model/retryTraining",
        { modelId: model._id },
        { headers: { Authorization: token } },
      );
      if (typeof data?.passesLeft === "number") {
        updateUser({ customModels: data.passesLeft });
      }
      toast.success(t("train.retry.retrying"));
      onRetried?.(data?.model);
    } catch (error) {
      const body = error?.response?.data ?? {};
      if (!body.code) console.error("retryTraining", error);
      setStep("idle");
      if (body.code === "no-pass") {
        // Their balance changed since the page loaded.
        updateUser({ customModels: 0 });
        setStep("confirm");
        return;
      }
      if (
        ["repeats", "files-missing", "no-dataset", "retry-cap"].includes(
          body.code,
        )
      ) {
        setRefusal({ code: body.code, message: body.message });
        return;
      }
      toast.error(body.message || t("train.retry.couldntStart"));
      if (body.code === "already-training" || body.code === "not-failed") {
        onRetried?.();
      }
    }
  };

  if (step === "idle") {
    return (
      <div className="tw-mt-auto tw-flex tw-flex-col tw-gap-2">
        <button
          type="button"
          onClick={() => {
            setStep("confirm");
            if (outOfPasses) {
              track("Training Retry Paywall Shown", {
                modelId: model._id,
                offer: offerCreator ? "creator" : "shop",
              });
            }
          }}
          className="tw-flex tw-h-12 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-[15px] tw-font-extrabold tw-text-white tw-transition-colors hover:tw-bg-jam-deep"
        >
          <RotateCcw size={16} strokeWidth={2.75} />
          {t("train.retry.tryAgain")}
        </button>
        <Link
          href="/train"
          className="tw-text-center tw-text-[13px] tw-font-extrabold tw-text-jam-muted tw-no-underline hover:tw-text-jam-ink"
        >
          {t("train.retry.orNewSamples")}
        </Link>
      </div>
    );
  }

  return (
    <div className="tw-mt-auto tw-flex tw-flex-col tw-gap-3 tw-rounded-3xl tw-bg-jam-lav tw-p-4">
      <p className="tw-m-0 tw-text-[13px] tw-font-bold tw-leading-snug tw-text-jam-ink">
        {unlimited
          ? t("train.retry.confirmUnlimited")
          : outOfPasses
            ? offerCreator
              ? t("train.retry.confirmOutCreator")
              : t("train.retry.confirmOut")
            : t("train.retry.confirmPass", { passes })}
      </p>
      <div className="tw-flex tw-gap-2">
        <button
          type="button"
          onClick={() => setStep("idle")}
          disabled={step === "busy"}
          className="tw-h-11 tw-flex-1 tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-white tw-text-sm tw-font-extrabold tw-text-jam-ink disabled:tw-opacity-50"
        >
          {t("train.retry.cancel")}
        </button>
        {outOfPasses ? (
          offerCreator ? (
            <button
              type="button"
              onClick={() => setUpsellOpen(true)}
              className="tw-h-11 tw-flex-[2] tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-sm tw-font-extrabold tw-text-white"
            >
              {t("train.retry.seeCreator")}
            </button>
          ) : (
            <Link
              href="/shop"
              className="tw-flex tw-h-11 tw-flex-[2] tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-purple tw-text-sm tw-font-extrabold tw-text-white tw-no-underline"
            >
              {t("train.retry.getPass")}
            </Link>
          )
        ) : (
          <button
            type="button"
            onClick={start}
            disabled={step === "busy"}
            className="tw-flex tw-h-11 tw-flex-[2] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-sm tw-font-extrabold tw-text-white disabled:tw-cursor-wait disabled:tw-opacity-70"
          >
            {step === "busy" && (
              <Spinner size={4} className="tw-border-3 tw-border-white" />
            )}
            {step === "busy"
              ? t("train.retry.starting")
              : unlimited
                ? t("train.retry.tryAgain")
                : t("train.retry.usePass")}
          </button>
        )}
      </div>
      {offerCreator && (
        <Link
          href="/shop"
          className="tw-text-center tw-text-xs tw-font-extrabold tw-text-jam-purple tw-no-underline"
        >
          {t("train.retry.orGetPass")}
        </Link>
      )}
      {upsellOpen && (
        <CreatorUpsellSheet
          open={upsellOpen}
          reason="train"
          onClose={() => setUpsellOpen(false)}
          onUpgraded={() => {
            setUpsellOpen(false);
            start();
          }}
        />
      )}
    </div>
  );
}
